import { ICropping } from "../fields"
import { LayerTypeId, LayerValueType } from "../generated/enums"
import { convertGeometryToOpenLayers, convertGeometryToWkt, Geometry2 } from "../geom"
import { Layer } from "../map/layer"
import { getDefaultEditStyleFunction, getSelectedStrokeStyle } from "../map/styles/defaults"
import { IMapTopologyService } from "../map/topo/types"
import { IMap, IMapContainer, OpenLayersStyleProvider } from "../map/types"
import { isObject, isUndefinedOrNull } from "../utils/utils"
import { DatalayerPoints } from "./datalayerPoints"
import { DatalayerZone } from "./datalayerZone"
import { IDataLayer, IDatalayerContext, IDatalayerKey, IDatalayerKeyItem, IDatalayerMapInstance, IDatalayerOptions, IDataLayerType, IDatalayerViewer, IDatalayerViewerFactory, IDataLayerZone, IDatalayerZoneMapInstance, LayerEditMode, ZoneTextLocation } from "./types"
import { cloneDatalayerZone, getDefaultZoneText, getLargestZone, getLayerDescription, getSelectedFillColor, getStyleProp } from "./utils"
import { DiscreteValuesViewer } from "./viewers/discrete"
import { NumericValuesViewer } from "./viewers/numeric"
import { ol_Feature, ol_style, ol_geom } from "@omnia/exports/openlayers";

export const labelMinOpacity = 20

let keySequence = 0

export class Datalayer<TLayer extends IDataLayer = IDataLayer, TData = any> extends Layer implements IDatalayerMapInstance {

    protected _datalayer: TLayer;

    public customType: IDataLayerType

    private _style: OpenLayersStyleProvider

    private _viewer: IDatalayerViewer;
    private _viewerFactory: IDatalayerViewerFactory;

    protected _zones: DatalayerZone[];

    private _transform: ((geom: ol_geom.Geometry) => void);

    private _showPoints: boolean;
    protected _pointsLayer: DatalayerPoints;

    private _key: IDatalayerKey & { version: number }

    protected _editMode: LayerEditMode;

    labelsVerticalOffset: number;

    showTooltips: boolean = true

    private _layerLabel: string
    private _layerLabelZone: DatalayerZone


    constructor(
        layer: TLayer,
        protected readonly _context: IDatalayerContext,
        options?: IDatalayerOptions,
        protected readonly layerData?: TData
    ) {

        super(options)

        this.selectable = false;

        if (layer) {
            this.initialize(layer);
        }

    }

    asGenericMapLayer(): Layer {
        return this
    }

    protected initialize(layer: TLayer) {

        const { context } = this

        this._datalayer = layer
        this._zones = []

        this.customType = context && layer.CustomTypeId
            ? context.loader.getCustomLayerType(layer.CustomTypeId, layer.TypeId)
            : undefined

        this.setLayerStyleProvider("default")

        if (layer.Zones && layer.Zones.length) {
            this.setupZones()
            this.applyTransform()
        }

        this.setupLayerType()

        this.refresh()

        var viewer = this.viewer
        if (viewer) viewer.refresh()

    }

    protected setupZones() {

        this._zones = this.createZones()

    }

    protected createZones() {

        let zones = this._datalayer.Zones.map(z => this.createZoneInstance(z));

        let zonesWithFeatures = zones.filter(z => !!z.feature)

        let emptyZones = zones.filter(z => (!z.feature) && z.zone.Geometry)

        let features = this.createFeaturesForZones(emptyZones)

        features.forEach(f => {
            const id = f.getId()
            const zone = zones.find(z => z.zone.DataLayerZoneId == id)
            zone.setFeature(f, zone.storedArea)
        });

        zonesWithFeatures.forEach(z => {
            let f = z.feature;
            f.setId(z.zone.DataLayerZoneId);
        })

        this._source.addFeatures(zonesWithFeatures.map(z => z.feature))

        return zones

    }

    protected createFeaturesForZones(zones: DatalayerZone[]) {

        return this.setSource<DatalayerZone>(
            zones,
            z => z.zone.Geometry,
            z => z.zone.DataLayerZoneId
        )

    }

    private setupLayerType() {

        let viewer: IDatalayerViewer = null
        let viewerFactory = this._context.viewManager.getCurrentViewer(this.typeId)

        if (viewerFactory) {
            viewer = viewerFactory.createViewer(this)
        }

        if (!viewer) {

            if (this.hasNumericValues()) {
                viewer = new NumericValuesViewer(this)
            }
            else if (this.hasDiscreteValues()) {
                viewer = new DiscreteValuesViewer(this)
            }

        }

        this._viewer = viewer
        this._viewerFactory = viewerFactory

        this.updateKey()

    }


    getCurrentViewerName(): string {
        let v = this._viewerFactory || this._context.viewManager.getCurrentViewer(this.typeId);
        if (v) return v.name;
    }

    public getViewer(): IDatalayerViewer {
        return this._viewer;
    }

    public setViewer(factory: IDatalayerViewerFactory) {

        this._viewer = (factory ? factory.createViewer(this) : null);
        this._viewerFactory = factory;

        this.refresh();

    }

    /**
     * Sets the style provider for this layer
     * Note: for some reason, this class does not rely on the style extensions available in the base class
     * (i.e., "styleProvider"), and also directly sets the style on the underlying OL layer object
     * It seems difficult to change this now, so this function has been added to make it possible to change
     * the style
     */
    public setLayerStyleProvider(provider: OpenLayersStyleProvider | undefined | "default") {

        const current = this._layer.getStyle()

        let style = provider === "default"
            ? this.getLayerStyleFunction(false)
            : provider

        this._style = style

        this.refreshStyle()

        return () => this._layer.setStyle(current)

    }

    private refreshStyle() {
        this._layer.setStyle(null)
        this._layer.setStyle(this._style)
    }

    public refreshViewer() {
        const v = this._viewer
        if (v) v.refresh()
    }

    public refresh() {
        this.refreshViewer()
        this.updateKey()
        this.refreshStyle()
    }

    protected createZoneInstance(zone: IDataLayerZone): DatalayerZone {

        return new DatalayerZone(this, zone);

    }

    protected setupPoints(map: IMapContainer) {

        const layer = this._datalayer
        if (!layer) return

        const { PointData: data, HasPointData: hasPointData } = layer

        if (data || hasPointData) {

            const source = data || (() => this.context.loader.loadLayerPoints(layer.DataLayerId))

            this._pointsLayer = new DatalayerPoints(
                source,
                this._viewer,
                this._showPoints,
                this.context.getCache<ol_style.Style[]>("pointStyles")
            )

            this._pointsLayer.addToMap(map.map)

        }

    }

    setVisible(value: boolean) {
        super.setVisible(value);
        if (this._pointsLayer) {
            this._pointsLayer.isVisible = value && this._showPoints
        }
    }

    get name(): string {
        return getLayerDescription(this._datalayer);
    }

    get datalayer() {
        return this._datalayer;
    }

    get datalayerId(): number {
        return this._datalayer.DataLayerId;
    }

    get fieldId(): number {
        return this._datalayer.FieldId;
    }

    get croppingId(): number {
        return this._datalayer.CroppingId;
    }

    get type(): IDataLayerType {
        return this._datalayer.Type;
    }

    get typeId(): LayerTypeId {
        return this._datalayer ? this._datalayer.TypeId : null;
    }

    get crop() {
        return this._datalayer && this._datalayer.Crop
    }

    get cropId(): number {
        return this._datalayer && this._datalayer.CropId
    }


    private _topmost: boolean

    get topmost() {
        return this._topmost
    }

    set topmost(topmost: boolean) {
        if (this._topmost !== topmost) {
            this._topmost = topmost
            this.refresh()
        }
    }

    showZoneNumbers() {
        return getStyleProp(this, s => s.ShowZoneNumbers, true)
    }

    showStroke(): boolean {

        const v = this.viewer

        const s = v ? v.showStroke : undefined

        return isUndefinedOrNull(s)
            ? getStyleProp(this, s => s.ShowStroke, true)
            : s

    }

    shouldShowLabels(): boolean {

        switch (this.typeId) {
            case LayerTypeId.EcScan:
            case LayerTypeId.Biomass:
                return false
        }

        if (this.opacity < labelMinOpacity) {
            return false
        }

        return this._topmost !== false

    }

    get zones(): DatalayerZone[] {
        return this._zones;
    }

    get points(): number[][] {
        return this._datalayer.PointData;
    }

    get context(): IDatalayerContext {
        return this._context;
    }

    getTopologyService(): IMapTopologyService {
        const { loader } = this._context
        return loader ? loader.getTopologyService() : null
    }

    get editMode(): LayerEditMode {
        return this._editMode;
    }

    set editMode(value: LayerEditMode) {
        this._editMode = value;
    }

    get viewer(): IDatalayerViewer {
        return this._viewer;
    }


    showPoints(show: boolean, map: IMapContainer, cluster?: boolean): void
    showPoints(show: false): void
    showPoints(): boolean
    showPoints(show?: boolean, map?: IMapContainer, cluster?: boolean): boolean {

        if (show === undefined) {
            return this._showPoints
        }

        this._showPoints = show

        if (show && (!this._pointsLayer)) {
            this.setupPoints(map)
        }

        if (this._pointsLayer) {
            this._pointsLayer.visible = show
            this._pointsLayer.cluster = cluster
        }

    }

    getPointsLayerExtent() {
        return this._pointsLayer?.getExtent()
    }

    canShowPoints() {

        const layer = this._datalayer
        if (!layer) return

        const { PointData: data, HasPointData: hasPointData } = layer

        return (hasPointData || Boolean(data))

    }

    hasDiscreteValues(): boolean {
        return (this.type && this.type.ValueType == LayerValueType.Discrete);
    }

    hasNumericValues(): boolean {
        return (this.type && this.type.ValueType == LayerValueType.Numeric);
    }

    get valueLabel(): string {
        const { type } = this
        return type ? (type.ValueLabel || type.Name) : undefined
    }

    get valueUnits(): string {
        const { type } = this
        return type?.Units
    }


    get valueEditTemplate(): string {
        return null;
    }

    get supportedValues(): any[] {
        const { type, _datalayer: l } = this
        return l.SupportedValues ?? type.Values
    }

    getTooltipTemplate(feature: any, data: any, editMode: boolean): string {
        return "app/datalayers/map/tooltip.html";
    }

    addToMap(map: IMap) {

        super.addToMap(map);

        if (this._pointsLayer) {
            this._pointsLayer.addToMap(map);
        }

    }

    removeFromMap(map: IMap) {

        super.removeFromMap(map);

        if (this._pointsLayer) {
            this._pointsLayer.removeFromMap(map);
        }

    }

    getLayerStyleFunction(selected: boolean): OpenLayersStyleProvider {

        const defaultStroke = new ol_style.Stroke({
            color: [0, 0, 0, 1],
            width: 1,
        });

        const selectedStroke = getSelectedStrokeStyle();

        return (feature: ol_Feature, resolution: number) => {

            const zone: DatalayerZone = feature.get("data");

            if (!zone) {
                return null
            }

            const layer = zone.layer

            if (!layer) {
                return null
            }

            if (!layer.getFeatureVisibility(feature)) {
                return null
            }

            let styles = layer.getZoneBaseStyle(feature, zone, selected, resolution)

            if (!styles) {

                let color = layer.getZoneFillColor(feature, zone);

                if (selected) {
                    color = layer.getSelectedFillColor(color);
                }

                const unselectedStroke = zone.isTransparent() || (zone.showStroke && layer.showStroke())
                    ? defaultStroke
                    : null

                styles = [
                    new ol_style.Style({
                        fill: color ? new ol_style.Fill({ color: color }) : undefined,
                        stroke: (selected ? selectedStroke.outer.getStroke() : unselectedStroke),
                    })
                ];

                if (selected) {
                    styles.push(selectedStroke.inner);
                }

                let pointStyles = layer.getZonePointStyle(feature, zone, selected, resolution);

                if (pointStyles) {
                    styles = styles.concat(pointStyles)
                }

            }

            const showText = layer.shouldShowLabels()

            if (showText && zone && layer.showLabels && layer.checkResolutionLabelFilter(resolution)) {

                const offset = layer.labelsVerticalOffset;

                const textStyles = layer.getTextStyles(zone, resolution, offset)

                styles = styles.concat(textStyles)

            }

            return styles;

        }


    }

    protected getTextStyles(zone: DatalayerZone, resolution: number, offsetY: number) {
        const styles = zone.getTextStyles(resolution, offsetY);
        return styles;
    }

    getEditStyleFunction(feature: ol_Feature, options?: {
        points?: boolean
        color?: number[]
        strokeColor?: number[],
        stroke?: boolean,
        text?: boolean
    }) {

        let { points, color, strokeColor, stroke, text } = options || { points: undefined, color: undefined, strokeColor: undefined, stroke: undefined, text: true }

        if (isUndefinedOrNull(points)) {
            let geom = feature.getGeometry()
            points = (geom instanceof ol_geom.Polygon) || (geom instanceof ol_geom.LineString) || (geom instanceof ol_geom.Circle)
        }

        const textStylesProvider = text
            ? (resolution: number) => this.getZoneFeatureTextStyles(feature, resolution)
            : null

        return getDefaultEditStyleFunction(feature, color, points, textStylesProvider, strokeColor, stroke)

    }

    private getZoneFeatureTextStyles(feature, resolution) {

        const zone = feature.get("data");

        if (zone instanceof DatalayerZone) {
            return zone.getTextStyles(resolution, 0)
        }

    }

    protected getZoneBaseStyle(feature: ol_Feature, zone: DatalayerZone, selected: boolean, resolution: number): ol_style.Style[] {
        return undefined
    }

    protected getZonePointStyle(feature: ol_Feature, zone: DatalayerZone, selected: boolean, resolution: number): ol_style.Style[] {
        return undefined
    }

    getZoneFillColor(feature: ol_Feature, zone: DatalayerZone) {

        let color = [255, 255, 255, 0.5];

        if (this._viewer) {
            color = this._viewer.getFeatureFillColor(feature);
        }

        return color;

    }

    protected getSelectedFillColor(color: number[]) {
        return getSelectedFillColor(color, this.opacity)
    }

    createNewZone(zone?: IDataLayerZone, setZoneNumber: boolean = true): DatalayerZone {

        zone = zone || <IDataLayerZone>{
            DataLayerZoneId: 0,
            Geometry: null
        };

        const inst = this.createZoneInstance(zone);

        if (setZoneNumber) {
            this.setZoneNumber(inst);
        }

        this._zones.push(inst);

        return inst;

    }

    createNewZoneWithGeometry(geometry: ol_geom.Geometry, data?: IDataLayerZone): DatalayerZone {

        const zone = this.createNewZone(data)

        const feature = super.createNewFeature(geometry, zone)

        zone.setFeature(feature)

        return zone

    }

    copyZones(zones: IDataLayerZone[], includeValue: boolean, clipping?: (geom: Geometry2) => Geometry2) {

        this.removeAllZones();

        let features = zones.map((zone, index) => {

            let geom = zone.Geometry;

            if (geom && clipping) {
                let clipped = clipping(geom);
                if (!clipped) return null;
                geom = clipped;
            }

            const newZone = cloneDatalayerZone(zone)

            let copy = this.createNewZone(newZone, false);

            let feature = copy.feature;

            if ((!feature) && geom) {
                feature = this.createFeature(copy, z => geom, z => 1 + index);
                copy.setFeature(feature);
            }

            if (includeValue) {
                copy.value = zone.Value;
            }

            return feature;

        }).filter(f => !!f);

        this._source.addFeatures(features);

        this.updateKey();

    }

    copyZonesFromCropping(cropping: ICropping[]) {

        const features = [];

        this.removeAllZones();

        cropping.filter(c => (!!c.Boundary)).forEach(c => {

            const copy = this.createNewZone();

            const feature = this.createFeature(copy, z => c.Boundary, z => 1 + features.length);

            copy.setFeature(feature);

            features.push(feature);

        });

        this._source.addFeatures(features);

        this.updateKey();

    }

    removeZone(zone: DatalayerZone) {
        this.removeZones([zone])
    }

    removeAllZones() {
        this.removeZones(this._zones.slice())
    }

    removeZones(zones: DatalayerZone[]) {

        for (const zone of zones) {

            this._zones.remove(z => z == zone)

            if (zone.feature) {
                super.removeFeature(zone.feature, true)
            }


        }

        this.updateKey()

    }

    protected setZoneNumber(zone: DatalayerZone) {
        const max = Math.max.apply(Math, this._zones.map(z => z.zoneNumber))
        zone.zoneNumber = 1 + (max < 0 ? 0 : max);
    }

    getGeometryAsWKT(geometry: ol_geom.Geometry) {

        return convertGeometryToWkt(geometry)

    }

    getGeometryFromWKT<T extends ol_geom.Geometry>(wkt: Geometry2): T {

        return convertGeometryToOpenLayers(wkt) as T

    }

    getUpdateModels() {
        return this.getUpdateModelsSafe(this.zones)
    }

    protected getUpdateModelsSafe(zones: DatalayerZone[]) {

        return zones
            .map(z => {

                try {
                    return z.getUpdateModel()
                }
                catch (e) {
                    console.error(e)
                    return null
                }

            })
            .filter(z => !!z)

    }

    private updateKey() {
        const key = this._viewer ? this._viewer.getKey() : undefined
        this._key = key ? { ...key, version: ++keySequence } : undefined
    }

    getKey() {
        const key = this._key
        return key ? { ...key } : undefined
    }

    getCurrentKey(): IDatalayerKeyItem[] {
        return this._key ? this._key.layerKey : []
    }

    getFullKey(): IDatalayerKeyItem[] {
        return this._key ? this._key.typeKey : []
    }

    protected getFeatureVisibility(feature: any): boolean {

        var viewer = this.viewer;
        if (!viewer) return true;

        var zone = <IDatalayerZoneMapInstance>feature.get("data");

        return viewer.getZoneVisibility(zone);

    }

    canEditFeatures(): boolean {
        return !(this.datalayer && this.datalayer.Calculated);
    }

    getZoneText(zone: DatalayerZone, location: ZoneTextLocation = ZoneTextLocation.MapFeature): string {

        const get = () => {

            if (zone.label) {
                return zone.label
            }

            if (isUndefinedOrNull(zone.value) && isObject(zone.zone.Data)) {
                return getDefaultZoneText(zone, location)
            }

            const v = this.viewer
            if (v) return v.getZoneText(zone, location)

        }

        let text = get()

        if ((zone == this._layerLabelZone) && this._layerLabel) {
            text = this._layerLabel + "\n" + text
        }

        return text

    }

    public setTransform(transform: ((geom: ol_geom.Geometry) => void)) {
        this._transform = transform;
        this.applyTransform();
    }

    private applyTransform() {

        let transform = this._transform;

        this.getFeatures().forEach(f => {

            let initGeom = f.get("initGeom");

            if (transform) {

                if (!initGeom) initGeom = f.getGeometry();

                let geom = initGeom.clone();
                transform(geom);
                f.setGeometry(geom);

            }
            else if (initGeom) {

                f.setGeometry(initGeom);

            }

            f.set("initGeom", initGeom);

        });

    }

    get labelsNoDeduplicate(): boolean {
        return this._labelOptions && (!this._labelOptions.deduplicate)
    }

    setLayerLabel(label: string) {

        this._layerLabel = label

        this._layerLabelZone = (label && label.length)
            ? getLargestZone(this)
            : undefined

        this.refresh()

    }

    measure(geom?: ol_Feature | ol_geom.Geometry) {
        const ms = this._context && this._context.measuringService
        return ms && ms.getDimensions(geom)
    }

    measureArea(geom?: ol_Feature | ol_geom.Geometry) {
        const ms = this._context && this._context.measuringService
        return ms && ms.getArea(geom)
    }

    measureLength(geom?: ol_Feature | ol_geom.Geometry) {
        const ms = this._context && this._context.measuringService
        return ms && ms.getLength(geom)
    }

}
